import { SESSION_LAYERS, type SessionLayer } from '@/components/admin/sessions/contracts';
import {
  type ObjectiveTone,
  decisionOriginLabel,
  objectiveTone,
  stepCapLabel,
} from '@/lib/session-labels';
import type {
  ExitAction,
  ExitCondition,
  ExitTrigger,
  GlobalRule,
  Guardrail,
  MatterQuantity,
  ObjectiveQuantity,
  ObjectiveStatus,
  PlayedCriterion,
  PlayedProtocol,
  ProtocolObjective,
  ProtocolStep,
  SessionDetail,
  SessionTurn,
  SnapshotCriterion,
  TurnOutcomeKind,
} from '@matter_extractor/shared';
import {
  emptyAnswer,
  isOpen,
  literalExtracts,
  normalizeMatter,
  objectiveCriterionGroups,
  objectiveProgress,
  objectiveState,
  rereadPass,
  scribeReportSchema,
} from '@matter_extractor/shared';

/**
 * What the conversation of §12.3 READS off a turn, in one pure module.
 *
 * ## Why it is here and not in the components
 *
 * The argument `session/figures.ts` of `shared` makes for the figures and
 * `lib/protocol-lanes.ts` makes for the geometry of the frise: jsdom has no
 * layout, so a component test that asserted a score against a threshold would be
 * asserting a render. Everything below is a resolution - a turn names an
 * identifier, the frozen protocol names the object - and each one has a way of
 * being wrong that looks perfectly healthy on screen: a score read against
 * today's threshold, a criterion nobody scored shown as a zero, a condition
 * named by its rank in the wrong step.
 *
 * ## Nothing here re-decides what the engine decided
 *
 * The rule `session/figures.ts` of `shared` states and this file keeps: a snapshot is
 * authoritative (§8.7), so a status is READ off the snapshot of its turn and a
 * verdict is `objectiveProgress` of `shared` - the same arithmetic the engine
 * ran, never a second spelling of it. What this module does is resolve names,
 * pair a turn with the one before it, and word what a condition says.
 */

/* --------------------------------------------------------- the layers in the URL */

/**
 * The sentinel that spells « no layer at all » in the address bar.
 *
 * An absent `couches` is the ARRIVAL state - État alone, the one layer open on
 * arrival (§12.3) - so « everything closed » needs a spelling of its own, or
 * closing the last switch would silently reopen État on the next reload. An
 * empty value cannot serve: a search parameter whose value is the empty string
 * is what a router drops on the way out.
 */
export const LAYERS_NONE = 'aucune';

/** The layer open when nobody has chosen: the sticky column, and it alone (§12.3). */
export const DEFAULT_OPEN_LAYERS: readonly SessionLayer[] = ['etat'];

function isLayer(value: string): value is SessionLayer {
  return (SESSION_LAYERS as readonly string[]).includes(value);
}

/**
 * The switches of the bar, as the address bar carries them.
 *
 * It never throws and never refuses: an unknown name is DROPPED, so a link
 * pasted from a deploy that had a seventh layer opens the six this one has
 * rather than showing a blank screen.
 */
export function readOpenLayers(param: string | undefined): ReadonlySet<SessionLayer> {
  if (param === undefined) return new Set(DEFAULT_OPEN_LAYERS);
  if (param === LAYERS_NONE) return new Set();
  return new Set(
    param
      .split(',')
      .map((name) => name.trim())
      .filter(isLayer),
  );
}

/**
 * The same, written back. In the CATALOGUE's order rather than in the order the
 * switches were pressed: two operators who opened the same three layers must be
 * able to paste the same link.
 */
export function writeOpenLayers(open: ReadonlySet<SessionLayer>): string {
  const names = SESSION_LAYERS.filter((layer) => open.has(layer));
  return names.length === 0 ? LAYERS_NONE : names.join(',');
}

/**
 * `?tour=` - the NUMBER of the turn a link ARRIVES on, 1-based, or null.
 *
 * An arrival and no longer a pin (issue #172): the page scrolls to that turn,
 * lights it and starts the État column on it, and from there the reader's eye
 * moves the column like on any other opening. The number and not the turn's
 * identifier: what the screen prints beside the reply is « Tour 4 », and a link
 * a reader pastes into a thread should say the same thing as the line it points
 * at. A number outside the conversation arrives nowhere rather than on the last
 * turn, which would make a stale link lie.
 */
export function readArrivalTurn(param: string | undefined, turnCount: number): number | null {
  if (param === undefined) return null;
  const parsed = Number.parseInt(param, 10);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > turnCount) return null;
  return parsed;
}

/* ------------------------------------------------------------ the frozen protocol */

/** One step of the frozen protocol, with the rank the screens print. */
export interface StepAt {
  readonly step: ProtocolStep;
  /** 1-based rank in the frozen protocol. */
  readonly index: number;
}

export function stepAt(protocol: PlayedProtocol | null, stepId: string): StepAt | null {
  if (protocol === null) return null;
  const rank = protocol.content.steps.findIndex((step) => step.id === stepId);
  const step = rank < 0 ? undefined : protocol.content.steps[rank];
  return step === undefined ? null : { step, index: rank + 1 };
}

/**
 * Is this the FAREWELL turn (issue #163, §8.1, §12.3)? The goodbye, a half-turn
 * nobody answers: `reply` null, no scribe, nothing evaluated.
 *
 * Two readings joined, each enough on its own. `reply === null` is the turn's
 * own record and the one the server writes (§8.7) - it holds even on a step the
 * frozen protocol no longer resolves. The step's `farewell` flag in the FROZEN
 * protocol covers the other side: it is what the version played said the step
 * was, whatever a later version made of it.
 *
 * The REREAD row (issue #165) is the one other turn whose `reply` is null, and
 * it is no goodbye: it is ruled out first, on its own flag, or every step that
 * rereads on entry would open on a turn reading « aucune réponse attendue ».
 */
export function isFarewellTurn(protocol: PlayedProtocol | null, turn: SessionTurn): boolean {
  if (turn.reread) return false;
  if (turn.reply === null) return true;
  return stepAt(protocol, turn.stepId)?.step.farewell ?? false;
}

/**
 * What the REREAD row says at rest (issue #165, §12.3): « Relecture à l'entrée
 * · relit <étape quittée> », then the objectives it turned to succeeded, or
 * « rien de rempli ».
 */
export interface RereadLine {
  /** The name of the step just left, whose last pass was reread; null when unknown. */
  readonly source: string | null;
  /** The objectives of the arriving step the reread succeeded, in protocol order. */
  readonly filled: readonly string[];
}

/**
 * The line of one reread row, READ off two snapshots and never re-decided.
 *
 * `previous` is the turn before the row - the one that left the departing
 * step (H94), so its step is what was reread. « Réussi d'entrée » is a CHANGE
 * of status, so it is read the way `turnReading` reads one: succeeded on the
 * row's snapshot and not on the one before. A step reentered after a replay
 * may already hold a succeeded objective, and naming it again would credit
 * the reread with what an earlier pass did.
 *
 * The names are the FROZEN protocol's; an objective it no longer resolves
 * keeps its identifier rather than vanishing from the line.
 */
export function rereadLine(
  detail: SessionDetail,
  turn: SessionTurn,
  previous: SessionTurn | undefined,
): RereadLine {
  const protocol = detail.cascade.protocol;
  const source =
    previous === undefined
      ? null
      : (stepAt(protocol, previous.stepId)?.step.name ?? previous.stepId);
  const catalogue = objectivesById(protocol);
  const arriving = stepAt(protocol, turn.stepId)?.step.objectives.map((objective) => objective.id);
  const ids = arriving ?? turn.snapshot.objectives.map((entry) => entry.objectiveId);
  const filled = ids
    .filter(
      (id) =>
        objectiveState(turn.snapshot, id).status === 'succeeded' &&
        (previous === undefined || objectiveState(previous.snapshot, id).status !== 'succeeded'),
    )
    .map((id) => catalogue.get(id)?.objective.name ?? id);
  return { source, filled };
}

/** One objective of the frozen protocol, with the step it belongs to. */
export interface ObjectiveAt {
  readonly objective: ProtocolObjective;
  readonly stepName: string;
  readonly stepIndex: number;
}

/**
 * Every objective of the protocol by id, across the steps.
 *
 * Across the steps and not inside the current one, because an invalidation may
 * aim at an objective of a previous step (H20, §8.4) and a direction may quote
 * one (§8.6): a lookup restricted to the turn's own step would print an
 * identifier where the decision named an objective.
 */
export function objectivesById(protocol: PlayedProtocol | null): ReadonlyMap<string, ObjectiveAt> {
  const found = new Map<string, ObjectiveAt>();
  if (protocol === null) return found;
  protocol.content.steps.forEach((step, rank) => {
    for (const objective of step.objectives) {
      found.set(objective.id, { objective, stepName: step.name, stepIndex: rank + 1 });
    }
  });
  return found;
}

/**
 * The FROZEN criteria of the session (§12.3), by id.
 *
 * Read off `cascade.criteria` and never off the library of today: « le nom d'un
 * critère ouvre sa fiche avec sa direction figée : c'est elle qui explique le
 * score, et elle peut différer de ce que dit le critère aujourd'hui ».
 */
export function criteriaById(detail: SessionDetail): ReadonlyMap<string, PlayedCriterion> {
  return new Map(detail.cascade.criteria.map((criterion) => [criterion.id, criterion]));
}

/* ----------------------------------------------------------- the reading of a turn */

/**
 * Why an extract never became matter - the three ways §8.1 lets one through the
 * report and not into the objective.
 *
 * `repeated` is per OCCURRENCE and not per text: the engine keeps the first
 * copy of a quotation and drops the ones after it (`literalExtracts`), so a
 * report that says the same sentence twice has one extract counted and one
 * « déjà compté ». `unread` is the objective's fault rather than the extract's -
 * the engine only reads the OPEN objectives of the current step, so anything
 * attributed to another one was never counted at all.
 */
export type ExtractDrop = 'absent' | 'repeated' | 'unread';

/** One verbatim extract of one objective, as this turn attributed it. */
export interface ExtractReading {
  readonly text: string;
  /** « ses extraits restent dans le journal marqués invalidés » (§8.4). */
  readonly invalidated: boolean;
  /** Null when the engine counted it; otherwise WHY it did not (§8.1). */
  readonly dropped: ExtractDrop | null;
}

/**
 * One criterion beside the bar it was judged against, as the snapshot froze it
 * (§8.7).
 *
 * Five of the seven fields come straight off `SnapshotCriterion` and none is
 * recomputed: a verdict, a score with its band, the threshold and the plancher
 * were all decided by the turn that wrote them, and the whole point of §8.7 is
 * that the reader replays rather than re-judges.
 */
export interface CriterionReading {
  readonly criterionId: string;
  /** The frozen name, or the identifier when the cascade no longer resolves it. */
  readonly name: string;
  readonly resolved: boolean;
  readonly verdict: SnapshotCriterion['verdict'];
  readonly score: SnapshotCriterion['score'];
  readonly band: SnapshotCriterion['band'];
  readonly threshold: SnapshotCriterion['threshold'];
  readonly plancher: SnapshotCriterion['plancher'];
  readonly satisfied: boolean;
  /** Where « passable » begins on the frozen scale (#110), or null. */
  readonly passable: SnapshotCriterion['passable'];
  readonly justification: string;
}

/**
 * The four fields that decide how one criterion READS - what the Objectifs
 * layer and the État column both ask of it, and nothing more.
 *
 * A narrow shape rather than `CriterionReading`, so the État column may ask the
 * same two questions of the snapshot entries it holds directly.
 */
export type JudgedCriterion = Pick<
  SnapshotCriterion,
  'verdict' | 'score' | 'threshold' | 'plancher'
>;

/**
 * Which of the two FORMS of §8.2 a criterion reads as - a verdict, or a score.
 *
 * Decided on `verdict` and not on `plancher`, and the difference is §4.1: a
 * criterion frozen BEFORE the grill of 2026-09-16 carries a score and no
 * plancher, and reading the form off the plancher would turn every one of them
 * into « pas encore jugé » on a screen replaying a session that judged it. A
 * verdict, on the other hand, only ever lands on the form that asked for one.
 */
export function isVerdictReading(criterion: JudgedCriterion): boolean {
  return criterion.verdict !== null;
}

/**
 * Has the scribe judged this criterion at all, in whichever form it took?
 *
 * Read off the JUDGEMENT and never off `satisfied`, which is false both for a
 * criterion that failed and for one nobody looked at: the tick and the colour
 * only mean something once there is a judgement to colour.
 */
export function isJudgedReading(criterion: Pick<JudgedCriterion, 'verdict' | 'score'>): boolean {
  return criterion.verdict !== null || criterion.score !== null;
}

/** The four tones a criterion reads in (§12.3, issue #110). */
export type CriterionTone = 'met' | 'passable' | 'short' | 'unjudged';

/**
 * Which TONE one criterion reads in: filled, passable, short of it, or not
 * judged yet (§12.3, issue #110).
 *
 * `satisfied` decides FIRST, and it is the turn's own figure, never recomputed
 * here (§8.7): « passable » only says how close a MISS came, from the lower
 * bound the frozen scale marked. A scale that marks none - every scale written
 * before #110 - and every verdict keep the two tones they always had.
 */
export function criterionTone(
  criterion: Pick<SnapshotCriterion, 'verdict' | 'score' | 'passable' | 'satisfied'>,
): CriterionTone {
  if (!isJudgedReading(criterion)) return 'unjudged';
  if (criterion.satisfied) return 'met';
  if (
    criterion.score !== null &&
    criterion.passable !== null &&
    criterion.score >= criterion.passable
  ) {
    return 'passable';
  }
  return 'short';
}

/**
 * H47, the garde au gel (§8.2), read off the fields that ARE it: a threshold
 * beside a criterion the engine judged BY VERDICT is a threshold it ignored.
 *
 * No flag on the snapshot, and that is the point: one fact spelled twice is two
 * producers to keep in step, and the two disagreed within the same lot.
 *
 * The verdict is what makes the reading safe. A requirement nobody has judged
 * yet carries the protocol's threshold beside a null plancher too - not because
 * the frozen version is unscored, but because the reader of §8.7 never knew the
 * scale: it has no judgement to explain, so it gets no sentence.
 */
export function hasIgnoredThreshold(criterion: JudgedCriterion): boolean {
  return criterion.threshold !== null && criterion.plancher === null && criterion.verdict !== null;
}

/**
 * One GROUP of an objective's criteria as the turn read it (issue #181, §7.2): a
 * criterion that stands alone, or an « l'un de » run - filled as soon as one of
 * its criteria is. `tone` is the group's, and it is read off the criteria's
 * own tones (which are the engine's `satisfied` and `passable`, §8.7): the
 * group is filled when one criterion is, passable when none is but one reaches
 * its passable bound, short when every one was judged and none gets there.
 */
export interface CriterionGroupReading {
  /** The shared label, or null for a criterion that stands alone. */
  readonly orGroup: string | null;
  /** A real « l'un de » group: two criteria or more under one label. */
  readonly either: boolean;
  readonly criteria: readonly CriterionReading[];
  readonly tone: CriterionTone;
  /** The criteria that carried the group (filled), or that came closest (passable). */
  readonly carriers: readonly CriterionReading[];
}

/**
 * The tone of a group of criteria, from the tones of its members: filled as soon
 * as one is, else passable as soon as one is, else short once any was judged.
 */
export function criterionGroupTone(tones: readonly CriterionTone[]): CriterionTone {
  if (tones.includes('met')) return 'met';
  if (tones.includes('passable')) return 'passable';
  return tones.every((one) => one === 'unjudged') ? 'unjudged' : 'short';
}

/**
 * Cut the criteria an objective read into the groups of its FROZEN requirements.
 *
 * The cut is `objectiveCriterionGroups` of `shared`, the one the engine and the
 * editor use. It needs the requirements, which carry the labels, and the
 * readings line up with them one for one; when the frozen protocol no longer
 * resolves the objective there are no labels left, and every reading stands
 * alone - the figures travel on the snapshot, the grouping does not.
 */
export function criterionGroupReadings(
  requirements: readonly { readonly orGroup?: string | undefined }[] | null,
  readings: readonly CriterionReading[],
): readonly CriterionGroupReading[] {
  const labelled = readings.map((reading, rank) => ({
    orGroup: requirements?.[rank]?.orGroup,
    reading,
  }));
  return objectiveCriterionGroups(labelled).map((group): CriterionGroupReading => {
    const criteria = group.criteria.map((entry) => entry.reading);
    const tones = criteria.map((criterion) => criterionTone(criterion));
    const tone = criterionGroupTone(tones);
    return {
      orGroup: group.orGroup,
      either: group.either && criteria.length > 1,
      criteria,
      tone,
      carriers: criteria.filter(
        (_, rank) => (tone === 'met' || tone === 'passable') && tones[rank] === tone,
      ),
    };
  });
}

/**
 * What a group of criteria says of itself, in the words of the mockup of #181:
 * « réussi par Intensité », « passable par … », « aucun critère ne passe ».
 * Null for a group nobody judged yet, and for a criterion that stands alone -
 * which has nothing to say about a group it is not in.
 */
export function criterionGroupMention(group: CriterionGroupReading): string | null {
  if (!group.either) return null;
  const by = group.carriers.map((criterion) => criterion.name).join(' et ');
  switch (group.tone) {
    case 'met':
      return `réussi par ${by}`;
    case 'passable':
      return `passable par ${by}`;
    case 'short':
      return 'aucun critère ne passe';
    case 'unjudged':
      return 'pas encore jugé';
  }
}

/** One objective the scribe named on this turn, judged. */
export interface ObjectiveReading {
  readonly objectiveId: string;
  readonly name: string;
  /** False when the frozen protocol no longer carries it: the name is the id. */
  readonly resolved: boolean;
  readonly mandatory: boolean;
  /** Did the ENGINE read it - an open objective of the turn's own step (§8.1)? */
  readonly counted: boolean;
  /** By TONE (#112): a réussi validated at the passable bar reads `passable`. */
  readonly statusBefore: ObjectiveTone;
  readonly statusAfter: ObjectiveTone;
  readonly extracts: readonly ExtractReading[];
  readonly criteria: readonly CriterionReading[];
  /** The same criteria cut into the « l'un de » groups of the frozen protocol (#181). */
  readonly groups: readonly CriterionGroupReading[];
  /** Counted on the CUMULATED matter (H19), by the engine's own arithmetic. */
  readonly quantity: MatterQuantity;
  /** Null when the frozen protocol poses no expectation anybody can read. */
  readonly expected: ObjectiveQuantity | null;
  /** Null for the same reason: an unknown expectation is not a reached one. */
  readonly quantityReached: boolean | null;
  readonly refused: boolean;
}

/** Everything the Objectifs layer of one turn shows. */
export interface TurnReading {
  readonly objectives: readonly ObjectiveReading[];
  /**
   * How many objectives the ENGINE had open on this turn - what it read.
   *
   * Not the length of the scribe's list: a report may name an objective the
   * engine skipped, and a header that counted those would announce more open
   * objectives than the step was holding.
   */
  readonly openObjectives: number;
  /** The scribe's verdict: did the person answer at all, matter or not (§8.2). */
  readonly answered: boolean;
  /** The code's verdict (H4 hybride): no literal extract landed AND the person did not answer. */
  readonly emptyAnswer: boolean;
  readonly refusalDetected: boolean;
  /** What the refusal aimed at, named; null when it aimed at nothing nameable. */
  readonly refusedObjective: string | null;
}

const EMPTY_READING: TurnReading = {
  objectives: [],
  openObjectives: 0,
  answered: true,
  emptyAnswer: false,
  refusalDetected: false,
  refusedObjective: null,
};

const UNKNOWN_QUANTITY: ObjectiveQuantity = { minSentences: null, minChars: null };

/**
 * The scribe's reading of one turn, resolved against the frozen protocol.
 *
 * `previous` is the turn before it in the conversation, and it is what makes
 * « le changement de statut » readable: a status is a state, so the CHANGE only
 * exists between two snapshots. The first turn compares against `empty`, which
 * is what an objective the engine has never met is (§8.7).
 *
 * A malformed turn reads as nothing at all - « ne change aucun statut » (§8.1) -
 * and the layer says so rather than drawing an empty block.
 *
 * ## The engine's two filters are the engine's, and they are asked for
 *
 * `applyReading` walks `step.objectives`, skips every one a settled status has
 * closed, and never looks at an objective of another step. A screen that walked
 * the scribe's own list instead would draw an extract the engine never read as
 * counted matter, and would announce more open objectives than the step held.
 * So « la réponse est vide » is `emptyAnswer` of `shared` - the engine's own
 * answer, not a second spelling of H4 - and every objective carries whether the
 * engine read it.
 */
export function turnReading(
  detail: SessionDetail,
  turn: SessionTurn,
  previous: SessionTurn | undefined,
): TurnReading {
  const report = turn.scribe;
  if (report === null) return EMPTY_READING;

  const found = stepAt(detail.cascade.protocol, turn.stepId);
  const catalogue = objectivesById(detail.cascade.protocol);
  const names = criteriaById(detail);
  const refusal = report.refusal;
  /*
    What the extracts are checked LITERAL against - the engine's own text. On a
    turn, the person's reply; the farewell turn's is null (§8.1), nothing was
    said to keep. On the REREAD row (issue #165, H95, H96) the reply is null
    too, and the text is the person's replies of the last pass of the step just
    left, joined as the server joins them - asked of `rereadPass` of `shared`,
    the function the server walks, so an extract the reading kept is never
    drawn here as absent from what was said.
  */
  const spoken = turn.reread
    ? previous === undefined
      ? ''
      : rereadPass(detail.turns, previous)
          .map((exchange) => exchange.person)
          .join('\n\n')
    : (turn.reply ?? '');

  // The state the ENGINE read this turn against. « Encore ouvert » is a fact
  // about the turn BEFORE this one, so it is read off its snapshot; the first
  // turn has none, and an objective nothing has met yet is `empty` (§8.7).
  const before = previous?.snapshot ?? { objectives: [] };
  // Null when the frozen protocol no longer carries the step: nobody can then
  // say which objectives the engine had open, and greying every extract of the
  // turn would be an answer nobody has.
  const read =
    found === null
      ? null
      : new Set(
          found.step.objectives
            .filter((objective) => isOpen(objectiveState(before, objective.id).status))
            .map((objective) => objective.id),
        );

  const objectives = report.objectives.map((said): ObjectiveReading => {
    const at = catalogue.get(said.objectiveId);
    // A step edited since may no longer carry the objective this turn read. The
    // turn stays legible: the identifier stands in for the name, and the frozen
    // scores still travel on the snapshot.
    const objective: ProtocolObjective = at?.objective ?? {
      id: said.objectiveId,
      name: said.objectiveId,
      mandatory: false,
      direction: '',
      analystLevel: null,
      criteria: [],
      quantity: UNKNOWN_QUANTITY,
    };
    const state = objectiveState(turn.snapshot, said.objectiveId);
    const progress = objectiveProgress(objective, state);
    const counted = read === null || read.has(said.objectiveId);
    const justifications = new Map(
      said.criteria.map((entry) => [entry.criterionId, entry.justification]),
    );
    const invalidated = new Set(
      state.extracts.filter((extract) => extract.invalidated).map((extract) => extract.text),
    );
    const kept = new Set(literalExtracts(spoken, said.extracts).map(normalizeMatter));
    const already = new Set<string>();
    /*
      The criteria of the FROZEN protocol when it still resolves the objective,
      and the ones the snapshot froze when it does not. The snapshot carries the
      score, the threshold and the verdict (§8.7), so a protocol edited since
      costs the NAMES of the requirements - never the figures a reader came for.
    */
    const scored: readonly SnapshotCriterion[] =
      at === undefined ? state.criteria : progress.criteria;

    const readings = scored.map((criterion): CriterionReading => {
      const played = names.get(criterion.criterionId);
      return {
        criterionId: criterion.criterionId,
        name: played?.content.name ?? criterion.criterionId,
        resolved: played !== undefined,
        verdict: criterion.verdict,
        score: criterion.score,
        band: criterion.band,
        threshold: criterion.threshold,
        plancher: criterion.plancher,
        satisfied: criterion.satisfied,
        passable: criterion.passable,
        justification: justifications.get(criterion.criterionId) ?? '',
      };
    });

    return {
      objectiveId: said.objectiveId,
      name: objective.name,
      resolved: at !== undefined,
      mandatory: objective.mandatory,
      counted,
      statusBefore:
        previous === undefined
          ? 'empty'
          : objectiveTone(objectiveState(previous.snapshot, said.objectiveId)),
      statusAfter: objectiveTone(state),
      extracts: said.extracts.map((text): ExtractReading => {
        // The engine collapses the blanks of an extract before it keeps it, so
        // the comparison has to collapse them too - otherwise a quotation the
        // turn accepted would read here as dropped.
        const collapsed = text.replace(/\s+/g, ' ').trim();
        const key = normalizeMatter(collapsed);
        const first = !already.has(key);
        already.add(key);
        return {
          text: collapsed,
          invalidated: invalidated.has(collapsed),
          dropped: !counted ? 'unread' : !kept.has(key) ? 'absent' : first ? null : 'repeated',
        };
      }),
      criteria: readings,
      groups: criterionGroupReadings(at === undefined ? null : objective.criteria, readings),
      quantity: progress.quantity,
      expected: at === undefined ? null : objective.quantity,
      quantityReached: at === undefined ? null : progress.quantityReached,
      refused: refusal.detected && refusal.objectiveId === said.objectiveId,
    };
  });

  return {
    objectives,
    openObjectives: read === null ? objectives.length : read.size,
    answered: report.answered,
    // The hybrid empty answer of §8.2, asked of the engine rather than
    // recomputed here. A step the frozen protocol no longer carries leaves the
    // flag off: the question cannot be put to the engine at all, and calling
    // every turn of it empty would be worse than saying nothing.
    // The farewell turn is not an empty answer (H82): nobody was asked one.
    emptyAnswer:
      found !== null && turn.reply !== null && emptyAnswer(turn.reply, found.step, before, report),
    refusalDetected: refusal.detected,
    refusedObjective:
      refusal.objectiveId === null
        ? null
        : (catalogue.get(refusal.objectiveId)?.objective.name ?? refusal.objectiveId),
  };
}

/* ------------------------------------------------------------ the turn's defect */

/** Which of the two defects a turn carries - the one thing that is read on it. */
export type TurnDefectKind = 'malformed' | 'truncated';

/** How one unreadable turn is named on the screen, and what it is told to do. */
export interface TurnDefect {
  /** Which defect it is - what a fold branches on, never `turn.truncated`. */
  readonly kind: TurnDefectKind;
  /** One word, in the bar and under the reply. */
  readonly label: string;
  /**
   * WHAT HAPPENED, in three words, and never a whole sentence.
   *
   * It is the half every fold needs and the only half they share: the mark
   * says « tour compté », the Objectifs fold says « aucune lecture », the
   * Garde-fous fold says « non évalués », and each of them is about ITS OWN
   * layer. Written whole, four sentences would say the same cause four times
   * and drift on the first correction.
   */
  readonly cause: string;
  /** One line - what happened, and what is left to read. */
  readonly note: string;
}

const MALFORMED: TurnDefect = {
  kind: 'malformed',
  label: 'Malformé',
  cause: 'scribe illisible',
  note: 'Le rapport du scribe n’a pas passé la validation : aucun extrait attribué, aucun score, aucun statut changé. La sortie brute est dans la couche Brut.',
};

const TRUNCATED: TurnDefect = {
  kind: 'truncated',
  label: 'Tronqué',
  cause: 'sortie coupée au plafond',
  note: 'La sortie du modèle a été coupée à son plafond de jetons avant la fin du rapport : aucun statut changé, et le texte brut est conservé tel quel dans la couche Brut.',
};

/**
 * The defect of one turn, NAMED - or null on a turn the scribe answered
 * readably (issue #96).
 *
 * ONE source for the four places that say it: the mark under the reply, the
 * Objectifs fold, the Garde-fous fold, the Brut fold. Each of them composes its
 * own sentence around `cause` and `label` and none of them reads `truncated` or
 * `malformed` off the turn - four spellings of one fact drift on the first
 * correction, and that fact is exactly what a reader opens this screen for.
 *
 * « Tronqué » is a SUBSET of « Malformé » and never anything else: the engine
 * played the same half-turn, the figures count the same malformed turn (§8.1),
 * and only the repair differs - a ceiling or a thinking to switch off on one
 * side, a prompt or a contract on the other.
 */
export function turnDefect(turn: SessionTurn): TurnDefect | null {
  if (!turn.malformed) return null;
  return turn.truncated ? TRUNCATED : MALFORMED;
}

/* ------------------------------------------------------------- the raw reading */

/**
 * What a seat's answer CUT at its token ceiling is (#96): not a fault of shape.
 * Said by the Brut layer of a scribe's turn and by the attempts of the noyau's
 * analyst (#152) - one sentence, so the two screens send a reader to the same
 * repair, the ceiling, and never to the prompt.
 */
export const TRUNCATED_OUTPUT_SENTENCE =
  'Sortie coupée au plafond de jetons du siège : le JSON s’arrête en plein rapport, ce n’est pas une faute de forme du modèle.';

/**
 * WHY a turn was marked malformed, read back off the text it stored.
 *
 * A turn records the flag and the raw output (§8.1, §8.7) and not the parse
 * error, because the error is a developer's string and the row is what survives
 * - so the diagnosis is recomputed here, against the contract of TODAY. That is
 * not a second source of truth: the flag stays the turn's, this only says what
 * the text does when it is read again.
 *
 * The third answer is the one worth having. A raw output that parses cleanly
 * under a turn flagged malformed means the CONTRACT moved since - a field added
 * to `scribeReportSchema`, a bound tightened - and that is a fact nobody can
 * discover any other way, least of all by re-reading the code that wrote the
 * row.
 *
 * It takes the DEFECT and not a boolean (issue #96), so that the one short
 * circuit it has - a cut output, which no re-reading can explain - is armed by
 * the same reading of the turn as every sentence on the screen. A defaulted
 * `truncated = false` was a second place where a caller could forget to pass
 * the fact, and the failure was a truthful parser error pointing at the wrong
 * repair.
 */
export function rawReadingError(scribeRaw: string, defect: TurnDefect | null = null): string {
  if (scribeRaw.trim() === '') return 'Le scribe n’a rien rendu du tout.';
  /*
    On a TRUNCATED turn (#96) the parser's own sentence - « Unexpected end of
    JSON input » - is true and misleading: it sends a reader hunting for a shape
    bug where the provider simply stopped writing. The turn already knows, so it
    is said before re-reading anything. The DEFECT is what says it, never
    `turn.truncated`: one reading of the two flags, in `turnDefect` above.
  */
  if (defect?.kind === 'truncated') return TRUNCATED_OUTPUT_SENTENCE;
  let parsed: unknown;
  try {
    parsed = JSON.parse(scribeRaw);
  } catch (error) {
    return `JSON illisible : ${error instanceof Error ? error.message : 'erreur inconnue'}`;
  }
  const read = scribeReportSchema.safeParse(parsed);
  if (read.success) {
    return 'Cette sortie relit pourtant sans erreur avec le contrat d’aujourd’hui : le contrat a changé depuis que le tour a été joué.';
  }
  const issue = read.error.issues[0];
  const where = issue === undefined || issue.path.length === 0 ? 'racine' : issue.path.join('.');
  return `Contrat du scribe non respecté en ${where} : ${issue?.message ?? 'champ refusé'}`;
}

/* ------------------------------------------------- the guardrails and the rules */

/** One guardrail on one turn: what the scribe saw, and where the series stands. */
export interface GuardrailReading {
  readonly guardrailId: string;
  /** What Réglages asks the scribe to detect, or the identifier when it is gone. */
  readonly detection: string;
  readonly resolved: boolean;
  /** « pendant N tours consécutifs » (§8.5); null on a guardrail Réglages lost. */
  readonly consecutiveTurns: number | null;
  readonly action: Guardrail['action'] | null;
  readonly triggered: boolean;
  readonly streak: number;
  /** A « signaler seulement » whose series reached its N on this turn (§8.5). */
  readonly incident: boolean;
}

/**
 * The guardrails of one turn, read off the TURN and not off Réglages.
 *
 * The list is what the turn recorded, plus every id the streaks carry: a
 * guardrail deleted since still judged this turn and its line must stay, and one
 * added since judged nothing - showing it as « non détecté » would be a lie
 * about a turn that never met it. Réglages is read for the NAME and the N, which
 * is all a turn does not carry (§8.5 freezes neither).
 */
export function turnGuardrails(
  turn: SessionTurn,
  guardrails: readonly Guardrail[],
): readonly GuardrailReading[] {
  const settings = new Map(guardrails.map((guardrail) => [guardrail.id, guardrail]));
  const incidents = new Set(turn.incidents.map((incident) => incident.guardrailId));
  const judged = turn.scribe?.guardrails ?? [];
  const ids = [
    ...new Set([
      ...judged.map((entry) => entry.guardrailId),
      ...Object.keys(turn.snapshot.guardrailStreaks),
    ]),
  ];

  return ids.map((guardrailId): GuardrailReading => {
    const known = settings.get(guardrailId);
    return {
      guardrailId,
      detection: known?.detection ?? guardrailId,
      resolved: known !== undefined,
      consecutiveTurns: known?.consecutiveTurns ?? null,
      action: known?.action ?? null,
      triggered: judged.find((entry) => entry.guardrailId === guardrailId)?.triggered ?? false,
      streak: turn.snapshot.guardrailStreaks[guardrailId] ?? 0,
      incident: incidents.has(guardrailId),
    };
  });
}

/** One global rule on one turn - observation, and nothing else (§8.5). */
export interface RuleReading {
  readonly ruleId: string;
  readonly text: string;
  readonly resolved: boolean;
  readonly violated: boolean;
}

/** The same arrangement for the rules: the turn's record, named by Réglages. */
export function turnRules(turn: SessionTurn, rules: readonly GlobalRule[]): readonly RuleReading[] {
  const settings = new Map(rules.map((rule) => [rule.id, rule]));
  return (turn.scribe?.rules ?? []).map((entry): RuleReading => {
    const known = settings.get(entry.ruleId);
    return {
      ruleId: entry.ruleId,
      text: known?.text ?? entry.ruleId,
      resolved: known !== undefined,
      violated: entry.violated,
    };
  });
}

/* ----------------------------------------------------------------- the decision */

/** Where a condition of §8.4 sits in the frozen protocol, so the screen can NAME it. */
export interface ConditionAt {
  readonly condition: ExitCondition;
  /** 1-based rank inside its step - the number §8.4 makes into data. */
  readonly index: number;
  readonly stepName: string;
  readonly stepIndex: number;
}

/**
 * The condition a decision names, found back in the frozen protocol.
 *
 * Searched across every step and not inside the turn's own, because a condition
 * carries a local id and the turn records that id alone: a search restricted to
 * the current step would print « condition inconnue » on a protocol that holds
 * it two steps away. Null when the frozen version no longer carries it - a
 * decision produced by something nobody can name any more is still a decision,
 * and the layer words it rather than hiding it.
 */
export function conditionOf(
  protocol: PlayedProtocol | null,
  id: string | null,
): ConditionAt | null {
  if (protocol === null || id === null) return null;
  for (const [rank, step] of protocol.content.steps.entries()) {
    const index = step.conditions.findIndex((condition) => condition.id === id);
    const condition = index < 0 ? undefined : step.conditions[index];
    if (condition !== undefined) {
      return { condition, index: index + 1, stepName: step.name, stepIndex: rank + 1 };
    }
  }
  return null;
}

/** The names a trigger or an action cites, resolved once for a whole screen. */
export interface ProtocolNames {
  readonly objective: (id: string) => string;
  readonly step: (id: string) => string;
}

export function protocolNames(protocol: PlayedProtocol | null): ProtocolNames {
  const objectives = objectivesById(protocol);
  const steps = new Map(
    (protocol?.content.steps ?? []).map((step, rank) => [step.id, `${rank + 1} · ${step.name}`]),
  );
  return {
    objective: (id) => objectives.get(id)?.objective.name ?? id,
    step: (id) => steps.get(id) ?? id,
  };
}

const CLASS_TRIGGER_LABEL: Readonly<Record<string, string>> = {
  'all-mandatory-succeeded': 'tous les obligatoires réussis',
  'all-optional-succeeded': 'tous les optionnels réussis',
  'optional-failed': 'un optionnel raté',
  'mandatory-failed': 'un obligatoire raté',
};

/** One trigger of §8.4, in the words the spec uses for it. */
export function triggerLabel(trigger: ExitTrigger, names: ProtocolNames): string {
  switch (trigger.kind) {
    case 'objective-status':
      return `« ${names.objective(trigger.objectiveId)} » passe à ${
        trigger.to === 'succeeded' ? 'réussi' : 'raté'
      }`;
    case 'objective-class':
      return CLASS_TRIGGER_LABEL[trigger.which] ?? trigger.which;
    case 'turn-cap-reached':
      return 'le plafond de tours est atteint';
    case 'attempts-exhausted':
      return 'les essais sont épuisés';
    default:
      return 'le quota de réponses vides est dépassé';
  }
}

/** One action of §8.4, in the words of its closed list. */
export function actionLabel(action: ExitAction, names: ProtocolNames): string {
  switch (action.kind) {
    case 'direction':
      return `direction d’IA : « ${action.text} »`;
    case 'invalidate-objective':
      return `invalider « ${names.objective(action.objectiveId)} »`;
    case 'close-step':
      return 'clôturer l’étape';
    case 'replay-step':
      return 'relancer l’étape';
    case 'go-to-step':
      return `aller à l’étape ${names.step(action.stepId)}`;
    case 'close-protocol':
      return 'clôturer le protocole';
    default:
      return 'fermer l’expérience';
  }
}

/**
 * A whole condition in one sentence: « et » inside a group, « ou » between them,
 * which is the shape §8.4 gives it and the shape the editor draws.
 */
export function conditionLabel(condition: ExitCondition, names: ProtocolNames): string {
  return condition.groups
    .map((group) => group.map((trigger) => triggerLabel(trigger, names)).join(' et '))
    .join(' ou ');
}

/* -------------------------------------------------------------- the head's facts */

/**
 * The model each seat ACTUALLY answered with, read off the calls of the turns.
 *
 * The last non-null one, and off the turns rather than off Réglages: §8.7 keeps
 * the id the API returned precisely because the one Réglages holds can differ -
 * an alias, a router serving another revision, a dated id quietly retired. The
 * LAST rather than the first, because a seat moved mid-session is a seat whose
 * current answer is the one somebody is reading the end of the conversation
 * against; the per-turn truth stays in the Appels layer, where every call is.
 *
 * A seat that answered nothing - every call failed - comes back null rather
 * than « inconnu », and the screens then simply do not print that seat.
 */
export function seatModels(turns: readonly SessionTurn[]): {
  readonly interviewer: string | null;
  readonly scribe: string | null;
} {
  let interviewer: string | null = null;
  let scribe: string | null = null;
  for (const turn of turns) {
    for (const call of turn.calls) {
      if (call.model === null) continue;
      if (call.seat === 'interviewer') interviewer = call.model;
      else scribe = call.model;
    }
  }
  return { interviewer, scribe };
}

/** The three pieces of « Clôturé par la logique implicite au tour 8 : … ». */
export interface ProtocolClosing {
  /** Everything before the bold, ARTICLE included - « le » and « la » differ. */
  readonly lead: string;
  /** Who decided, set in bold by the caller: « logique implicite », « garde-fou ». */
  readonly emphasis: string;
  readonly tail: string;
}

/**
 * What CLOSED the conversation, or null while it is still open - the ONE
 * sentence the parcours (§12.2) and the conversation (§12.3) both print.
 *
 * One function and not one per screen, which is the whole point of it living
 * here: two spellings of « qu'est-ce qui a clos la conversation » drifted on
 * three separate facts - the branches they treated, whether they named the
 * frozen `closeReason`, and how they numbered the turn - so two screens
 * described one event two ways.
 *
 * Read off the decision of the LAST turn, where §8.7 records both halves -
 * WHERE the session went (`outcome`) and WHO decided it (`producedBy`) - and
 * never off the session's status: a session can be `terminée` because the
 * person reached the last page long after the protocol closed itself, so the
 * status answers a question about the experience where this one answers a
 * question about the conversation. The session's own `closeReason` is appended
 * when it carries one: it is the frozen label of the rule, the condition or the
 * guard, and it is the half that names the thing rather than its kind.
 *
 * Null when the last turn decided to continue or to replay: a protocol still
 * open was closed by nothing, and a sentence saying so would describe an event
 * that has not happened.
 *
 * The CAP the decision was taken on rides in the tail when there is one (§8.3,
 * issue #97): « qui a clos » and « contre quoi » are the pair a reader needs,
 * and the parcours has no Moteur layer to tell the second half - without it,
 * the one screen that opens on a session would still have to deduce a step
 * closed by the budget from the fact that it was neither of the two others.
 *
 * ## A conversation that ended on the FAREWELL step (issue #163, §8.1)
 *
 * The farewell turn is written as a `close-protocol` of the implicit logic,
 * and it is always the last one: read as is, every session sent to the goodbye
 * by a fallback, a condition or « conclure par l'adieu » would say « la logique
 * implicite » and hide what actually ended it. So on a farewell turn the
 * sentence is built from the turn BEFORE it - the decision that led to the
 * goodbye, with its origin and its cap - and says the goodbye came after:
 * « … au tour 3, puis l'adieu au tour 4 ». A protocol whose first step is the
 * farewell has no turn before it, and there the implicit logic is the truth.
 */
export function protocolClosingSentence(detail: SessionDetail): ProtocolClosing | null {
  const last = detail.turns[detail.turns.length - 1];
  if (last === undefined) return null;

  const reason = detail.session.closeReason;
  const ending = reason === null ? '.' : ` : ${reason}.`;

  if (isFarewellTurn(detail.cascade.protocol, last)) {
    const led = detail.turns[detail.turns.length - 2];
    // The farewell turn's own number, counted from one like every other.
    const goodbye = `l’adieu au tour ${last.index + 1}`;
    if (led === undefined) {
      return {
        lead: 'Clôturé par la ',
        emphasis: decisionOriginLabel(last.decision.producedBy),
        tail: ` : ${goodbye}${ending}`,
      };
    }
    const ledCap = stepCapLabel(led.decision.cap);
    return {
      lead: `Clôturé par ${led.decision.producedBy.kind === 'guardrail' ? 'le' : 'la'} `,
      emphasis: decisionOriginLabel(led.decision.producedBy),
      tail: ` au tour ${led.index + 1}${ledCap === null ? '' : `, au ${ledCap}`}, puis ${goodbye}${ending}`,
    };
  }

  const opening = CLOSING_OPENING[last.decision.outcome.kind];
  if (opening === undefined) return null;

  // « le garde-fou » but « la condition de sortie »: the article belongs to the
  // origin and not to the sentence, so it travels with the lead rather than
  // being guessed from the label.
  const article = last.decision.producedBy.kind === 'guardrail' ? 'le' : 'la';
  // The three labels are worded to follow « au »: « au plafond de tours », « au
  // quota de réponses vides », « au budget IA de l'étape ».
  const cap = stepCapLabel(last.decision.cap);
  return {
    lead: `${opening} ${article} `,
    emphasis: decisionOriginLabel(last.decision.producedBy),
    // `index` is 0-based on the row (§8.7) and the screens count turns from one.
    // The stored index and never the length of the list: a conversation read
    // from a page of turns would otherwise number the last one by how many
    // were loaded.
    tail: ` au tour ${last.index + 1}${cap === null ? '' : `, au ${cap}`}${ending}`,
  };
}

/**
 * The outcomes that END a conversation, and the words each one opens with.
 *
 * `close-step` is here and it is the branch a screen forgets: closing the LAST
 * step closes the protocol and the engine normalises it (§8.3), so a
 * `close-step` on the final turn is a step that closed and a conversation that
 * simply has no turn after it. `continue` and `replay-step` are absent, and
 * `go-to-step` with them: all three leave the protocol open.
 */
const CLOSING_OPENING: Readonly<Partial<Record<TurnOutcomeKind, string>>> = {
  'close-protocol': 'Clôturé par',
  'close-experience': 'L’expérience a été fermée par',
  'close-step': 'Dernière étape clôturée par',
};

/* -------------------------------------------------------------------- the budget */

/**
 * Where the cumulated cost of the attempt stands against the budget of §11.2, in
 * whole percent, capped at 100.
 *
 * Null on a budget of zero or less rather than a division: §11.2 lets an
 * operator turn the budget off, and « 0 % of nothing » is a bar that would
 * always read full or always read empty depending on the rounding.
 */
export function budgetShare(stepCostEur: number, budgetEur: number | null): number | null {
  if (budgetEur === null || budgetEur <= 0) return null;
  return Math.min(100, Math.round((stepCostEur / budgetEur) * 100));
}

/* --------------------------------------------- the history of one objective */

/** One extract of the snapshot a turn carries, with the turn that first carried it. */
export interface ExtractAt {
  readonly text: string;
  readonly invalidated: boolean;
  /** 1-based number of the first turn whose snapshot held this extract. */
  readonly firstTurn: number;
}

/**
 * The extracts of one objective in the snapshot of turn `upTo`, each with the
 * turn that brought it (§12.3, issue #172: « chacun précédé du numéro du tour
 * qui l'a porté le premier »).
 *
 * ## By POSITION, the rule of the Matière tab
 *
 * `experience/matter.ts` of the backend attributes an extract to the first
 * snapshot that held its position, because the engine only ever APPENDS to an
 * objective's matter and an invalidation flags in place (§8.4). Matching on the
 * text alone would be wrong the day a person says the same sentence again after
 * it was invalidated: two entries, the same text, and the live one would borrow
 * the turn of the dead one. The position is kept only while its text is the
 * same, so a snapshot that ever rewrote a position - none does today - restarts
 * that position on its own turn instead of lying about where it came from.
 *
 * Pure and over the snapshots alone: nothing is computed that a turn does not
 * already carry (§8.7).
 */
export function extractOrigins(
  turns: readonly SessionTurn[],
  objectiveId: string,
  upTo: number,
): readonly ExtractAt[] {
  let origins: readonly ExtractAt[] = [];
  turns.slice(0, Math.max(0, upTo)).forEach((turn, position) => {
    const { extracts } = objectiveState(turn.snapshot, objectiveId);
    const before = origins;
    origins = extracts.map((extract, at) => {
      const was = before[at];
      return {
        text: extract.text,
        invalidated: extract.invalidated,
        firstTurn: was !== undefined && was.text === extract.text ? was.firstTurn : position + 1,
      };
    });
  });
  return origins;
}

/** When one objective became réussi, and when it stopped being, up to a turn. */
export interface ObjectivePassages {
  /** The turns (1-based, oldest first) where it passed from any other status to réussi. */
  readonly passages: readonly number[];
  /** The turns where it left réussi, with the status it fell to - an invalidation (§8.4). */
  readonly losses: readonly { readonly turn: number; readonly status: ObjectiveStatus }[];
}

/**
 * The passages of one objective to « réussi », at turn `upTo` or before
 * (§12.3, issue #172).
 *
 * A passage is a TRANSITION between two consecutive snapshots, the one before
 * the first turn being the empty state an objective starts from. The passable
 * réussi of #112 is a réussi like any other here: its status is `succeeded`,
 * only a reader paints it apart. A reread on entry (#165) is a turn with a
 * snapshot, so a passage it made counts like one a reply made.
 */
export function objectivePassages(
  turns: readonly SessionTurn[],
  objectiveId: string,
  upTo: number,
): ObjectivePassages {
  const passages: number[] = [];
  const losses: { turn: number; status: ObjectiveStatus }[] = [];
  let was: ObjectiveStatus = 'empty';
  turns.slice(0, Math.max(0, upTo)).forEach((turn, position) => {
    const { status } = objectiveState(turn.snapshot, objectiveId);
    if (status === 'succeeded' && was !== 'succeeded') passages.push(position + 1);
    if (status !== 'succeeded' && was === 'succeeded') losses.push({ turn: position + 1, status });
    was = status;
  });
  return { passages, losses };
}

/**
 * The turn a réussi objective of the column leads to: the LAST passage to
 * réussi at turn `upTo` or before, the one that explains the state on screen
 * when an invalidation undid a first one (§12.3). Null when the objective is
 * not réussi at `upTo` - a raté or a vide leads nowhere.
 */
export function lastPassage(
  turns: readonly SessionTurn[],
  objectiveId: string,
  upTo: number,
): number | null {
  const shown = turns[upTo - 1];
  if (shown === undefined || objectiveState(shown.snapshot, objectiveId).status !== 'succeeded') {
    return null;
  }
  const { passages } = objectivePassages(turns, objectiveId, upTo);
  return passages[passages.length - 1] ?? null;
}
